import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronRight } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';

import { ServiceType } from '@/data/service-items';
import { PorfolioItem, portfolioItems } from '@/features/portfolio';
import { translate } from '@/translations';

export const FeaturedWorkSection = () => {
  const { t } = useTranslation('home');
  const [selectedCategory, setSelectedCategory] = useState<ServiceType>(ServiceType.all);
  const portfolioItemsToShow = portfolioItems.filter((item) => item.showInHome);
  const allCategories = [
    ServiceType.all,
    ...new Set(portfolioItemsToShow.flatMap((portfolioItem) => portfolioItem.categories)),
  ];
  const filteredItems = portfolioItemsToShow.filter(
    (item) => selectedCategory === 'all' || item.categories.includes(selectedCategory)
  );

  const firstColumnItems = filteredItems.filter((_, index) => index % 2 === 0);
  const secondColumnItems = filteredItems.filter((_, index) => index % 2 !== 0);

  const navigate = useNavigate();

  const showPortfolioItem = (item: PorfolioItem) => {
    navigate(`/portfolio/${item.key}`);
  };

  return (
    <div className="section bg-gradient-to-r from-background1 to-background2">
      <div className="section-title">
        <h1>{t('featuredWorkTitle')}</h1>
      </div>

      <div className="flex items-center text-sm md:text-base gap-5 mt-8 overflow-x-auto">
        {allCategories.map((category) => (
          <button
            key={category}
            className={`flex text-text hover:text-hoverButton uppercase px-2 rounded-lg ${selectedCategory === category ? 'bg-black text-white' : ''}`}
            onClick={() => setSelectedCategory(category)}
          >
            {translate(`serviceItems:${category}:title`)}
          </button>
        ))}
      </div>

      <div className="lg:flex lg:justify-center lg:items-center mt-5 lg:mt-10">
        <div className="grid grid-col-1 sm:grid-cols-2 gap-10 lg:gap-24 2xl:gap-48">
          <div className="2xl:w-[592px]">
            {firstColumnItems.map((item) => (
              <div
                className="w-full mb-14 sm:mb-20 lg:mb-28 cursor-pointer"
                key={item.title}
                onClick={() => showPortfolioItem(item)}
              >
                <div className="flex justify-center">
                  <img
                    src={item.thumbnailSrc}
                    alt={item.title}
                    className="rounded-xl h-[30vh] object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="flex justify-between items-start mt-5">
                  <div className="text-base sm:text-lg font-semibold">
                    <h1>{item.title}</h1>
                  </div>
                  <div className="text-sm lg:text-base text-text">
                    <div className="flex items-center gap-2">
                      <span>{t('viewNow')}</span>
                      <FaChevronRight />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="sm:mt-16">
            {secondColumnItems.map((item) => (
              <div
                className="w-full mb-14 sm:mb-20 lg:mb-28 cursor-pointer"
                key={item.title}
                onClick={() => showPortfolioItem(item)}
              >
                <div className="flex justify-center">
                  <img
                    src={item.thumbnailSrc}
                    alt={item.title}
                    className="rounded-xl h-[35vh] object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="flex justify-between items-start mt-5">
                  <div className="text-base sm:text-lg font-semibold">
                    <h1>{item.title}</h1>
                  </div>
                  <div className="text-sm lg:text-base text-text">
                    <div className="flex items-center gap-2">
                      <span>{t('viewNow')}</span>
                      <FaChevronRight />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-center items-center">
        <div className="action-button h-12 w-72 xl:h-14 rounded-xl bg-button hover:bg-hoverButton text-white text-lg font-semibold">
          <Link className="flex justify-center items-center h-12 w-72 xl:h-14" to={'/portfolio'}>
            {t('seeMore')}
          </Link>
        </div>
      </div>
    </div>
  );
};
